<script>
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { editorState } from "@app/store/editorStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import { autoReplaceState } from "@app/store/autoReplaceStore.svelte.js";
import { untrack } from "svelte";
import Quill from "quill";
import { clickoutside } from "@svelte-put/clickoutside";
import debounce from "just-debounce-it";
import quillClasses from "@cclib/dict/quillClasses.js";
import insertEv from "@cclib/functions/eventGroups/insertEvent.js";
import splitIntoParagraphs from "@cclib/functions/quill/splitIntoParagraphs.js";
import textLengthLimiter from "@cclib/functions/quill/textLengthLimiter.js";
import extractTextFromHtml from "@cclib/functions/quill/extractTextFromHtml.js";
import convertToHtml from "@cclib/functions/quill/convertToHtml.js";
import colorLookup from "@cclib/dict/colors.js";
import * as m from "@app/paraglide/messages.js";
import { environment } from "@app/store/envStore.svelte.js";
import {
    KEYBOARD_SHORTCUTS_UPDATED_EVENT,
    getStoredKeyboardShortcuts,
} from "@app/lib/keyboardShortcuts.js";

let { event, eventIndex } = $props();
let timer, paragraphs;
let quill;
let initialSelectionTimer;
let cursorRestoreTimer;
let disableEditingTimer;
let insertEventTimer;
let currentText = "";
let lastSyncedText = "";
let lastSyncedAlignment;
let lastSyncedRtl = false;
let quillDirty = false;
let customBindingKeys = [];

function buildQuillShortcutBindings(shortcutGroups) {
    let shortcutBindings = {
        tab: {
            key: "Tab",
            handler: function () {
                return false;
            },
        },
    };

    shortcutGroups.forEach((shortcutGroup) => {
        shortcutGroup.shortcuts.forEach((shortcut) => {
            if (
                shortcut.keyCmd &&
                !shortcut.keyCmd.includes("+") &&
                shortcut.keyCmd !== "ArrowDown" &&
                shortcut.keyCmd !== "ArrowUp" &&
                shortcut.keyCmd !== "ArrowLeft" &&
                shortcut.keyCmd !== "ArrowRight"
            ) {
                shortcutBindings[shortcut.keyCmd] = {
                    key: shortcut.keyCmd,
                    handler: function () {
                        return false;
                    },
                };
            }
        });
    });

    return shortcutBindings;
}

let bindings = buildQuillShortcutBindings(
    getStoredKeyboardShortcuts(environment.mac),
);

function applyBindingsToQuill(quillInstance, shortcutGroups) {
    if (!quillInstance?.keyboard) {
        return;
    }

    customBindingKeys.forEach((key) => {
        delete quillInstance.keyboard.bindings[key];
    });
    customBindingKeys = [];

    const updatedBindings = buildQuillShortcutBindings(shortcutGroups);

    Object.entries(updatedBindings).forEach(([name, binding]) => {
        if (name === "tab") {
            return;
        }

        quillInstance.keyboard.bindings[name] = binding;
        customBindingKeys.push(name);
    });
}

function handleKeyboardShortcutsUpdated(e) {
    const shortcutGroups =
        e.detail?.shortcuts || getStoredKeyboardShortcuts(environment.mac);

    bindings = buildQuillShortcutBindings(shortcutGroups);
    applyBindingsToQuill(quill, shortcutGroups);
}

function setupQuillEditor(node) {
    return untrack(() => {
    let destroyed = false;
    quill = new Quill(node, {
        theme: "bubble",
        modules: {
            keyboard: {
                bindings: bindings,
            },
            toolbar: [
                ["bold", "underline", "italic"],
                [
                    {
                        color: colorLookup.colors,
                    },
                    {
                        background: colorLookup.colors,
                    },
                ],
                ["clean"],
            ],
            history: {
                maxStack: 0,
                userOnly: true,
            },
        },
        formats: [
            "bold",
            "underline",
            "italic",
            "align",
            "direction",
            "color",
            "background",
        ],
    });
    window.quillEditor = quill;
    customBindingKeys = Object.keys(bindings).filter((key) => key !== "tab");

    window.addEventListener(
        KEYBOARD_SHORTCUTS_UPDATED_EVENT,
        handleKeyboardShortcutsUpdated,
    );

    applyEventToQuill();

    try {
        if (editorState.editing) {
            initialSelectionTimer = setTimeout(() => {
                if (destroyed) {
                    return;
                }

                quill.setSelection(
                    quill.getLength(),
                    0,
                    "silent",
                );
                editorState.editing = true;
            }, 50);
        } else {
            quill.blur();
        }
    } catch (err) {
        console.log("Error setting initial selection: ", err.message);
    }

    const toolbarContainer = quill.getModule("toolbar").container;
    const toolbarMouseDownHandler = (e) => {
        e.preventDefault();
    };
    toolbarContainer.addEventListener("mousedown", toolbarMouseDownHandler);

    const debouncedTextChange = debounce(
        (delta, oldDelta, source) => {
            if (destroyed) {
                return;
            }

            if (
                source === "user" &&
                delta.ops[1] &&
                (delta.ops[1].insert === "\n" || delta.ops[1].insert === " ")
            ) {
                let updateFlag = false;
                let offset = 0;
                autoReplaceState.forEach((list) => {
                    if (!list.enabled) {
                        return;
                    }

                    Object.keys(list.replacements).forEach((replacement) => {
                        let re = new RegExp(
                            `(?<=^|\\s)${replacement}(?=\\s|$)`,
                            "g",
                        );
                        oldDelta.ops.forEach((op) => {
                            if (!op.insert) {
                                return;
                            }

                            let matches = re.exec(op.insert);
                            if (matches) {
                                op.insert = op.insert.replace(
                                    re,
                                    list.uppercase && matches.index === 0
                                        ? capitalizeFirstLetter(
                                              list.replacements[replacement],
                                          )
                                        : list.replacements[replacement],
                                );
                                offset =
                                    list.replacements[replacement].length -
                                    replacement.length;
                                updateFlag = true;
                            }
                        });
                    });
                });

                if (updateFlag) {
                    let range = quill.getSelection();
                    delta = quill.setContents(oldDelta, "user");
                    syncEventText();

                    if (range) {
                        cursorRestoreTimer = setTimeout(() => {
                            if (destroyed) {
                                return;
                            }

                            quill.setSelection(
                                range.index + offset,
                                0,
                                "silent",
                            );
                        }, 0);
                    }

                    return;
                }
            }

            if (
                editorState.autoInsertEvent &&
                delta.ops[1] &&
                delta.ops[1].insert === "\n" &&
                source === "user"
            ) {
                paragraphs = splitIntoParagraphs(currentText);
                if (
                    paragraphs.length >
                    eventGroupState[projectState.selected].maxLines
                ) {
                    var range = quill.getSelection();
                    var length = quill.getLength();
                    event.text = paragraphs
                        .splice(
                            0,
                            eventGroupState[projectState.selected].maxLines,
                        )
                        .join("");

                    insertEvent({
                        text: paragraphs
                            .filter((p) => {
                                return !p.includes("<br>");
                            })
                            .join(""),
                        alignment: event.alignment,
                        xPos: event.xPos,
                        yPos: event.yPos,
                        style: event.style,
                        start: editorState.insertStartOnInsert
                            ? player.currentTime
                            : false,
                        end: editorState.insertStartOnInsert
                            ? player.currentTime +
                              eventGroupState[projectState.selected].minDuration
                            : false,
                    });

                    let matches = paragraphs[0].match(/<p[^>]*>(.*?)<\/p>/);
                    if (
                        matches &&
                        matches[1] !== "<br>" &&
                        range?.index !== length - 2
                    ) {
                        eventGroupState[projectState.selected].selected = [
                            eventIndex,
                        ];

                        let delta = quill.clipboard.convert({
                            html: event.text,
                        });
                        quill.setContents(delta, "silent");
                        quill.formatLine(
                            0,
                            99999,
                            "align",
                            event.alignment === "left" ? "" : event.alignment,
                            "silent",
                        );
                        syncEventText();
                    }

                    pauseWhileTyping();
                    saveToHistory();
                }
            } else if (
                editorState.autoLineBreak &&
                delta.ops[1] &&
                delta.ops[1].insert !== " " &&
                delta.ops[1].insert !== "\n" &&
                source === "user"
            ) {
                paragraphs = extractTextFromHtml(currentText);

                let lineErrors = paragraphs.findIndex((p) => {
                    return (
                        p.length >=
                        eventGroupState[projectState.selected].maxChars
                    );
                });

                if (lineErrors > -1) {
                    let cursorRange = quill.getSelection();
                    paragraphs = textLengthLimiter(
                        paragraphs,
                        eventGroupState[projectState.selected].maxChars,
                    );

                    if (!editorState.autoInsertEvent) {
                        let delta = quill.clipboard.convert({
                            html: convertToHtml(paragraphs.join("\n"), [
                                quillClasses.align[event.alignment],
                            ]),
                        });

                        quill.setContents(delta, "silent");
                        quill.formatLine(
                            0,
                            99999,
                            "align",
                            event.alignment === "left" ? "" : event.alignment,
                            "silent",
                        );
                        syncEventText();

                        cursorRestoreTimer = setTimeout(() => {
                            if (destroyed) {
                                return;
                            }

                            quill.setSelection(
                                cursorRange
                                    ? cursorRange.index
                                    : quill.getLength(),
                                0,
                                "silent",
                            );
                        }, 0);
                    } else if (editorState.autoInsertEvent) {
                        let delta = quill.clipboard.convert({
                            html: convertToHtml(
                                paragraphs
                                    .splice(
                                        0,
                                        eventGroupState[projectState.selected]
                                            .maxLines,
                                    )
                                    .join("\n"),
                                [quillClasses.align[event.alignment]],
                            ),
                        });
                        quill.setContents(delta, "silent");
                        quill.formatLine(
                            0,
                            99999,
                            "align",
                            event.alignment === "left" ? "" : event.alignment,
                            "silent",
                        );
                        syncEventText();

                        if (paragraphs.length > 0) {
                            insertEventTimer = setTimeout(() => {
                                if (destroyed) {
                                    return;
                                }

                                insertEvent({
                                    text: convertToHtml(paragraphs.join("\n"), [
                                        quillClasses.align[event.alignment],
                                    ]),
                                    alignment: event.alignment,
                                    xPos: event.xPos,
                                    yPos: event.yPos,
                                    style: event.style,
                                    vertical: event.vertical,
                                    start: editorState.insertStartOnInsert
                                        ? player.currentTime
                                        : false,
                                    end: editorState.insertStartOnInsert
                                        ? player.currentTime +
                                          eventGroupState[projectState.selected]
                                              .minDuration
                                        : false,
                                });
                            }, 0);
                        }
                    }

                    pauseWhileTyping();
                    saveToHistory();
                } else {
                    event.text = currentText;
                    pauseWhileTyping();
                    saveToHistory();
                }
            } else {
                event.text = currentText;

                if (source === "user") {
                    pauseWhileTyping();
                    saveToHistory();
                }
            }
        },
        250,
        false,
    );

    const textChangeHandler = (delta, oldDelta, source) => {
        if (source === "silent") {
            return;
        }

        quillDirty = true;
        syncEventText();

        debouncedTextChange(delta, oldDelta, source);
    };

    const selectionChangeHandler = function (range, oldRange, source) {
        if (source == "user" && range) {
            editorState.editing = true;
        }
    };

    quill.on("text-change", textChangeHandler);
    quill.on("selection-change", selectionChangeHandler);

    return () => {
        destroyed = true;
        if (quillDirty) {
            syncEventText({ guardExternalUpdates: true });
        }
        clearTimeout(timer);
        clearTimeout(initialSelectionTimer);
        clearTimeout(cursorRestoreTimer);
        clearTimeout(disableEditingTimer);
        clearTimeout(insertEventTimer);
        quill.off("text-change", textChangeHandler);
        quill.off("selection-change", selectionChangeHandler);
        window.removeEventListener(
            KEYBOARD_SHORTCUTS_UPDATED_EVENT,
            handleKeyboardShortcutsUpdated,
        );

        if (toolbarContainer) {
            toolbarContainer.removeEventListener(
                "mousedown",
                toolbarMouseDownHandler,
            );
        }

        if (window.quillEditor === quill) {
            window.quillEditor = undefined;
        }
    };
    });
}

function getEventRtl() {
    return !!eventGroupState[projectState.selected]?.rtl;
}

function applyEventToQuill({ preserveSelection = false } = {}) {
    if (!quill || !event) {
        return;
    }

    const range = preserveSelection ? quill.getSelection() : undefined;
    const eventText = event.text || "";
    const rtl = getEventRtl();
    const delta = quill.clipboard.convert({ html: eventText });

    quill.setContents(delta, "silent");
    quill.formatLine(
        0,
        99999,
        "align",
        event.alignment === "left" ? "" : event.alignment,
        "silent",
    );
    quill.formatLine(
        0,
        99999,
        "direction",
        rtl ? "rtl" : false,
        "silent",
    );

    currentText = quill.root.innerHTML;
    lastSyncedText = eventText;
    lastSyncedAlignment = event.alignment;
    lastSyncedRtl = rtl;
    quillDirty = false;

    if (range) {
        const length = quill.getLength();
        const index = Math.min(range.index, length - 1);

        quill.setSelection(
            index,
            Math.min(range.length, length - index - 1),
            "silent",
        );
    }
}

$effect(() => {
    const eventText = event?.text || "";
    const alignment = event?.alignment;
    const rtl = !!eventGroupState[projectState.selected]?.rtl;

    if (!quill || !event) {
        return;
    }

    if (
        eventText !== lastSyncedText ||
        alignment !== lastSyncedAlignment ||
        rtl !== lastSyncedRtl
    ) {
        const range = quill.getSelection();
        const delta = quill.clipboard.convert({ html: eventText });

        quill.setContents(delta, "silent");
        quill.formatLine(
            0,
            99999,
            "align",
            alignment === "left" ? "" : alignment,
            "silent",
        );
        quill.formatLine(
            0,
            99999,
            "direction",
            rtl ? "rtl" : false,
            "silent",
        );

        currentText = quill.root.innerHTML;
        lastSyncedText = eventText;
        lastSyncedAlignment = alignment;
        lastSyncedRtl = rtl;
        quillDirty = false;

        if (range) {
            const length = quill.getLength();
            const index = Math.min(range.index, length - 1);

            quill.setSelection(
                index,
                Math.min(range.length, length - index - 1),
                "silent",
            );
        }
    }
});

function syncEventText({ guardExternalUpdates = false } = {}) {
    if (!quill || !event) {
        return;
    }

    if (guardExternalUpdates && event.text !== lastSyncedText) {
        quillDirty = false;
        return;
    }

    currentText = quill.root.innerHTML;
    event.text = currentText;
    lastSyncedText = currentText;
    lastSyncedAlignment = event.alignment;
    lastSyncedRtl = getEventRtl();
    quillDirty = false;
}

async function insertEvent(options) {
    try {
        if (!eventGroupState[projectState.selected]) {
            return;
        }

        if (editorState.insertEndOnInsert) {
            event.end = player.currentTime;
        }
        eventGroupState[projectState.selected] = insertEv(
            eventGroupState[projectState.selected],
            options,
            eventIndex + 1,
        );
        eventGroupState[projectState.selected].selected = [eventIndex + 1];

        const selectedIndex = eventIndex + 1;

        // Dispatch custom event to scroll to the selected event
        window.dispatchEvent(
            new CustomEvent("scrollToVirtualListItem", {
                detail: { index: selectedIndex },
            }),
        );

        insertHistoryAction({
            name: "insert event",
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });
    } catch (err) {
        console.log(err, err.message);
    }
}

const saveToHistory = function () {
    editorState.editing = true;
    insertHistoryAction({
        name: "update text",
        eventGroup: projectState.selected,
        snapshots: [
            {
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            },
        ],
    });
};

async function pauseWhileTyping() {
    if (editorState.pauseWhileTyping && player.state.playing) {
        player.pause();
        if (timer) {
            clearTimeout(timer);
        }

        timer = setTimeout(async function () {
            await player.play();
        }, editorState.pauseWhileTypeDuration || 750);
    }
}

function capitalizeFirstLetter(string) {
    return string[0].toUpperCase() + string.slice(1);
}

function disableEditing() {
    syncEventText();
    let inputs = ['input', 'select', 'button', 'textarea'];
    let activeElementTag = document.activeElement.tagName.toLowerCase();
    if (!inputs.includes(activeElementTag)) {
        document.body.focus();
    }

    disableEditingTimer = setTimeout(() => {
        editorState.editing = false;        
    }, 50);
}

function selectEditor() {
    if (quill && !quill.getSelection()) {
        quill.setSelection(
            quill.getLength(),
            0,
            "silent",
        );
    }
}
</script>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<div
    id="QuillEditor"
    class="QuillEditor rounded"
    role="region"
    aria-label={m.event_text_editor_aria_label({eventIndex})}
    style="font-family: '{eventGroupState[projectState.selected]?.fontFamily ||
        'plexMono'}' !important; line-height: 101%; font-size: {eventGroupState[
        projectState.selected
    ]?.fontSize || 18}px;"
    {@attach setupQuillEditor}
    use:clickoutside
    onclickoutside={() => disableEditing()}
    onclick={() => selectEditor()}>
</div>

<style lang="css">
.QuillEditor {
    background-color: #000;
    color: #fff;
    height: auto;
    min-height: 160px;
    word-break: break-word;
}
</style>
